import React, { useEffect, useState } from 'react';
import { MapPin, PartyPopper, ArrowRight, CheckCircle2, Sparkles } from 'lucide-react';

// Zeige die Sektion bis 7 Tage nach der Eröffnung besonders prominent, danach dauerhaft in kompakter Form
const OPENING_DATE = new Date('2026-08-29T10:00:00+02:00').getTime();
const SEVEN_DAYS_MS = 7 * 24 * 60 * 60 * 1000;

// Kleine Konfetti-Partikel
const Confetti = () => {
  const pieces = Array.from({ length: 24 });
  const colors = ['#f59e0b', '#dc2626', '#10b981', '#3b82f6', '#a855f7', '#eab308'];
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden="true">
      {pieces.map((_, i) => {
        const delay = (i * 0.35) % 6;
        const left = (i * 7) % 100;
        const color = colors[i % colors.length];
        return (
          <span
            key={i}
            className="absolute top-[-20px] block w-2 h-3 rounded-sm opacity-70"
            style={{
              left: `${left}%`,
              backgroundColor: color,
              animation: `confetti-fall 5s linear infinite`,
              animationDelay: `${delay}s`,
              transform: `rotate(${(i * 40) % 360}deg)`
            }}
          ></span>
        );
      })}
      <style>{`
        @keyframes confetti-fall {
          0% { transform: translateY(-20px) rotate(0deg); opacity: 0; }
          10% { opacity: 0.9; }
          100% { transform: translateY(110vh) rotate(720deg); opacity: 0; }
        }
      `}</style>
    </div>
  );
};

export const NowOpenSection = () => {
  const [showConfetti, setShowConfetti] = useState(false);

  useEffect(() => {
    const isWithin7Days = Date.now() - OPENING_DATE < SEVEN_DAYS_MS && Date.now() >= OPENING_DATE;
    setShowConfetti(isWithin7Days);
  }, []);

  return (
    <section
      className="relative py-16 md:py-20 overflow-hidden"
      data-testid="now-open-section"
      style={{
        background:
          'radial-gradient(ellipse at top left, rgba(16,185,129,0.15) 0%, transparent 55%), radial-gradient(ellipse at bottom right, rgba(245,158,11,0.12) 0%, transparent 55%), linear-gradient(135deg, #0f172a 0%, #1e293b 100%)'
      }}
    >
      {showConfetti && <Confetti />}

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Badge */}
        <div className="flex justify-center mb-6">
          <div className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-gradient-to-r from-emerald-400 to-emerald-500 text-gray-900 font-bold text-sm uppercase tracking-[0.2em] shadow-xl shadow-emerald-500/30">
            <PartyPopper className="w-4 h-4" />
            Wir haben eröffnet!
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          {/* Linke Seite: Grafik */}
          <div className="flex justify-center lg:justify-end order-2 lg:order-1">
            <div className="relative">
              {/* Glow */}
              <div className="absolute inset-0 bg-gradient-to-br from-emerald-500/40 via-amber-500/30 to-emerald-600/40 blur-3xl scale-90"></div>

              {/* Karte */}
              <div className="relative w-[300px] md:w-[360px] rounded-3xl overflow-hidden shadow-2xl bg-white transform hover:scale-105 transition-transform duration-500">
                {/* Header grün */}
                <div className="bg-gradient-to-br from-emerald-500 via-emerald-600 to-emerald-700 text-white py-5 px-6 flex items-center gap-3">
                  <CheckCircle2 className="w-8 h-8 flex-shrink-0" strokeWidth={2.5} />
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.25em] font-bold opacity-90">Standort geöffnet</p>
                    <p className="text-lg font-black leading-tight">Omoda &amp; Jaecoo</p>
                  </div>
                </div>
                {/* Body */}
                <div className="p-6 space-y-4">
                  <div className="flex items-start gap-3">
                    <MapPin className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-gray-900 font-bold">Florapark Magdeburg</p>
                      <p className="text-gray-500 text-sm">Olvenstedter Graseweg 37<br />39128 Magdeburg</p>
                    </div>
                  </div>
                  <div className="pt-4 border-t border-gray-100 flex items-center gap-2 text-sm text-gray-600">
                    <Sparkles className="w-4 h-4 text-amber-500" />
                    Seit 29. August 2026 für Sie da
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Rechte Seite: Text */}
          <div className="text-center lg:text-left order-1 lg:order-2">
            <p className="text-emerald-300 text-xs md:text-sm uppercase tracking-[0.25em] font-bold mb-4">
              Neuer Standort · Neue Marken
            </p>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white leading-tight mb-5">
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-300 to-amber-500">
                Omoda &amp; Jaecoo
              </span>{' '}
              jetzt im Florapark Magdeburg
            </h2>
            <p className="text-base md:text-lg text-white/70 leading-relaxed mb-8">
              Danke an alle Besucherinnen und Besucher unserer Eröffnung am
              29.&nbsp;August! Unser neuer Standort ist ab sofort dauerhaft für
              Sie geöffnet. Entdecken Sie <strong className="text-white">Omoda&nbsp;9</strong>,{' '}
              <strong className="text-white">Jaecoo&nbsp;7</strong> und{' '}
              <strong className="text-white">Jaecoo&nbsp;5</strong> bei einer
              persönlichen Beratung – wir freuen uns auf Ihren Besuch.
            </p>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row gap-3 justify-center lg:justify-start">
              <a
                href="https://oj.autohaus-wilke.de"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white font-semibold shadow-xl shadow-red-500/30 hover:shadow-red-500/50 transition-all duration-300 hover:scale-[1.03]"
                data-testid="nowopen-cta-website"
              >
                Zur Omoda &amp; Jaecoo Website
                <ArrowRight className="w-4 h-4" />
              </a>
              <a
                href="https://www.google.com/maps/dir//Olvenstedter+Graseweg+37,+39128+Magdeburg"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl bg-white/10 hover:bg-white/20 border border-white/20 text-white font-semibold backdrop-blur-sm transition-all duration-300"
                data-testid="nowopen-cta-route"
              >
                <MapPin className="w-4 h-4" />
                Route planen
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
